/**
 * Tiny best-effort UA → "Browser · OS" label for the devices list. No
 * dependency: only the handful of combinations our users actually have.
 *
 * Native app sessions carry no browser UA. flashka-app sends
 * `Flashka/<version> (<os> <osVersion>; <model>)` (see its `user-agent.ts`);
 * sessions created before that header shipped carry the platform HTTP
 * stack's default — `Flashka/1 CFNetwork/… Darwin/…` on iOS, `okhttp/…` on
 * Android — which is recognised too so old rows do not read "Unknown device".
 */
export function parseUserAgent(ua: string | null | undefined): string {
  if (!ua) return 'Unknown device';

  const app = parseAppUserAgent(ua);
  if (app) return app;

  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : null;

  const os = /iPhone|iPad/.test(ua)
    ? 'iOS'
    : /Android/.test(ua)
      ? 'Android'
      : /Macintosh/.test(ua)
        ? 'macOS'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Linux/.test(ua)
            ? 'Linux'
            : null;

  if (!browser && !os) return 'Unknown device';
  if (browser && os) return `${browser} · ${os}`;
  return browser ?? os ?? 'Unknown device';
}

const APP_LABEL = 'Flashka app';

function parseAppUserAgent(ua: string): string | null {
  // Browser UAs all start with "Mozilla/" — never mistake one for the app.
  if (/^Mozilla\//.test(ua)) return null;

  const explicit = /^Flashka\/\S+\s*\(([^)]*)\)/.exec(ua);
  if (explicit) {
    const inner = explicit[1] ?? '';
    const os = /iOS|iPadOS/i.test(inner)
      ? 'iOS'
      : /Android/i.test(inner)
        ? 'Android'
        : null;
    return os ? `${APP_LABEL} · ${os}` : APP_LABEL;
  }
  // Platform HTTP-stack defaults (pre-header sessions).
  if (/CFNetwork\/|Darwin\//.test(ua)) return `${APP_LABEL} · iOS`;
  if (/okhttp\//i.test(ua)) return `${APP_LABEL} · Android`;
  return null;
}
